import {
  Trailer,
  TrailerSoilQuantity,
  TriaxleSoilQuantity,
} from "../../utils/constants";
import { HaulageRecordFields, SoilQuantity } from "../../interface/HaulageRecordFields";
import { HaulageTicketPayload } from "../../interface/api/HaulageTicketPayload";
import { SmallQuantityHaulageTicketPayload } from "../../interface/api/SmallQuantityHaulageTicketPayload";

export const SoilQualityText =
  "Reg 153/04 Table 2 Agricultural and Other Property Use, except for EC and SAR, with Reg 406/19 exceptions for PAHs and VOCs.";

// Weighed loads report gross/net/tare; unweighed ones fall back to a nominal figure per truck type.
const soilQuantityFor = (
  weight: number | null | undefined,
  tareWeight: number | null | undefined,
  truckTypeId: number | null | undefined
): SoilQuantity => {
  if (weight) {
    const tare = Number(tareWeight ?? 0);
    return { kind: "weighed", grossKg: weight + tare, netKg: weight, tareKg: tare };
  }
  return {
    kind: "estimated",
    text: truckTypeId === Trailer ? TrailerSoilQuantity : TriaxleSoilQuantity,
  };
};

// Main Pit. Every key is set (coalescing to null) so the rendered record is unchanged.
export const toMainPitFields = ({
  ticket,
  truck,
  contact,
}: HaulageTicketPayload): HaulageRecordFields => ({
  ticketNumber: ticket?.ticketNumber ?? null,

  projectAddress: ticket?.jobSite ?? null,
  soilQuality: SoilQualityText,
  saltImpactedNote: ticket?.isSaltImpacted
    ? " Load is to be managed as salt-impacted."
    : " Load is not identified as salt-impacted.",
  contractor: ticket?.contractorCompanyName ?? null,
  contactName:
    (ticket?.isSoilFLOLoad
      ? ticket?.siteContactName
      : ticket?.contractorOfficeContactName) ?? null,
  contactNumber:
    (ticket?.isSoilFLOLoad
      ? ticket?.siteContactNumber
      : ticket?.contractorOfficeContactNumber) ?? null,
  contactEmail: ticket?.contractorOfficeContactEmail ?? null,
  material: ticket?.materialName ?? null,
  soilQuantity: soilQuantityFor(ticket?.weight, truck?.tareWeight, ticket?.truckTypeId),
  timeLoaded: ticket?.timeLoaded ?? null,
  haulingCompany: ticket?.transportCompanyName ?? null,
  driverName: ticket?.truckDriverName ?? null,
  licencePlate: ticket?.truckLicensePlate ?? null,

  receivingContactName: ticket?.isReceived
    ? contact?.name ?? null
    : null,
  receivingContactNumber: ticket?.isReceived
    ? contact?.phone ?? null
    : null,
  receivingContactEmail: contact?.email ?? null,
  timeReceived: ticket?.timeReceived ?? null,

  isSoilFLOLoad: ticket?.isSoilFLOLoad ?? false,
});

// Small Quantities. Keys the SQ ticket does not capture are deliberately omitted so those
// rows do not render: soil quality, salt-impacted, contractor contact details, material
// and soil quantity.
export const toSmallQuantityFields = ({
  ticket,
  contact,
}: SmallQuantityHaulageTicketPayload): HaulageRecordFields => ({
  ticketNumber: ticket?.ticketNumber ?? null,

  projectAddress: ticket?.jobSite ?? null,
  contractor: ticket?.contractorCompanyName ?? null,
  timeLoaded: ticket?.timeLoaded ?? null,
  haulingCompany: ticket?.haulageCompanyName ?? ticket?.transportCompanyName ?? null,
  driverName: ticket?.truckDriverName ?? null,
  licencePlate: ticket?.truckLicensePlate ?? null,
  truckNumber: ticket?.truckNumber ?? null,

  receivingContactName: ticket?.isReceived
    ? contact?.name ?? null
    : null,
  receivingContactNumber: ticket?.isReceived
    ? contact?.phone ?? null
    : null,
  receivingContactEmail: contact?.email ?? null,
  timeReceived: ticket?.timeReceived ?? null,

  isSoilFLOLoad: false,
});
